import { chance, rand } from "../timing";
import type { Dictionary } from "./dictionary";
import { applyProduction, createSeed } from "./grammar";

/** Shared reference to the loaded dictionary (set via setDictionary). */
let dict: Dictionary | null = null;

/** Called once after the dictionary loads to enable POS-aware generation. */
export function setDictionary(d: Dictionary): void {
  dict = d;
}

function choice<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)] as T;
}

function isWordChar(char: string): boolean {
  return /[A-Za-z0-9']/.test(char);
}

function clampIndex(text: string, index: number): number {
  return Math.max(0, Math.min(text.length, index));
}

function wordRanges(text: string): Array<[number, number]> {
  return [...text.matchAll(/[A-Za-z0-9']+[.,!?;:…]*/g)]
    .map((match) => {
      if (match.index == null) return null;
      return [match.index, match.index + match[0].length] as [number, number];
    })
    .filter((range): range is [number, number] => range !== null);
}

const FALLBACK_WORDS = [
  "human", "bot", "shared", "space", "cursor", "draft", "edit", "text",
  "hello", "note", "tiny", "blue", "random", "growing", "canvas", "alive",
  "typing", "select", "delete", "replace", "move", "click", "future", "signal",
  "paper", "soft", "prompt", "idea", "loop", "trace", "shape", "pixel",
  "small", "story", "marker", "world", "flow", "plain", "quick", "slow",
  "thought", "field", "line", "window",
] as const;

function randomFallbackWords(min = 1, max = 3): string {
  const n = Math.floor(rand(min, max + 1));
  const out: string[] = [];
  for (let i = 0; i < n; i++) out.push(choice(FALLBACK_WORDS));
  return out.join(" ");
}

export function randomWords(min = 1, max = 3): string {
  if (dict) {
    const n = Math.floor(rand(min, max + 1));
    const posChoices = ["noun", "verb", "adjective", "adverb"];
    const out: string[] = [];
    for (let i = 0; i < n; i++) {
      const pos = choice(posChoices);
      out.push(dict.random(pos) ?? choice(FALLBACK_WORDS));
    }
    return out.join(" ");
  }
  return randomFallbackWords(min, max);
}

export function randomPhrase(min?: number, max?: number): string {
  if (dict) {
    return createSeed(dict);
  }
  let s = randomWords(min, max);
  if (chance(0.22)) s += choice([".", "?", "!", "..."]);
  return s;
}

export function appendChunk(current: string): string {
  if (dict) {
    const result = applyProduction(current, dict);
    if (result && result.text.startsWith(current)) {
      // Return only the appended portion (difference from original)
      return result.text.slice(current.length);
    }
    // Fallback: append a random word with spacing
    const word = dict.random(choice(["noun", "verb", "adjective", "adverb"])) ?? choice(FALLBACK_WORDS);
    let s = word;
    if (current && !/\s$/.test(current) && /^[a-z]/i.test(s)) s = " " + s;
    return s;
  }
  let s = randomPhrase(1, chance(0.5) ? 2 : 4);
  if (current && !/\s$/.test(current) && /^[a-z]/i.test(s)) s = " " + s;
  return s;
}

export function insertChunk(text: string, index: number): string {
  const safeText = text || "";
  const safeIndex = index ?? safeText.length;
  const before = safeIndex > 0 ? safeText[safeIndex - 1] ?? "" : "";
  const after = safeIndex < safeText.length ? safeText[safeIndex] ?? "" : "";

  let chunk = randomWords(1, 2);

  if (safeIndex > 0 && /[\w.,!?;:…]/.test(before)) {
    chunk = " " + chunk;
  }
  if (safeIndex < safeText.length && /\w/.test(after)) {
    chunk += " ";
  }
  if (/\s/.test(before) && chunk.startsWith(" ")) {
    chunk = chunk.slice(1);
  }
  if (/\s/.test(after) && chunk.endsWith(" ")) {
    chunk = chunk.slice(0, -1);
  }

  return chunk;
}

export function wordBoundaries(text: string): number[] {
  const bounds = new Set<number>([0, text.length]);
  for (let i = 1; i < text.length; i++) {
    const prev = /\w/.test(text[i - 1] ?? "");
    const curr = /\w/.test(text[i] ?? "");
    if (prev !== curr) bounds.add(i);
  }
  return [...bounds].sort((a, b) => a - b);
}

export function snapToWordBoundary(text: string, index: number): number {
  const bounds = wordBoundaries(text);
  let best = bounds[0] ?? 0;
  for (const b of bounds) {
    if (Math.abs(b - index) < Math.abs(best - index)) best = b;
  }
  return best;
}

export function pickRange(text: string): [number, number] {
  if (!text.length) return [0, 0];
  const matches = [...text.matchAll(/[A-Za-z0-9']+[.,!?;:…]*/g)];
  if (matches.length && chance(0.75)) {
    const i0 = Math.floor(rand(0, matches.length));
    const span = Math.floor(rand(1, Math.min(3, matches.length - i0) + 1));
    const startM = matches[i0];
    const endM = matches[i0 + span - 1];
    if (!startM || !endM || startM.index == null || endM.index == null) {
      return [0, text.length];
    }
    return [startM.index, endM.index + endM[0].length];
  }

  const bounds = wordBoundaries(text);
  if (bounds.length >= 2) {
    const i = Math.floor(rand(0, bounds.length - 1));
    const j = Math.floor(rand(i + 1, Math.min(bounds.length, i + 4)));
    return [bounds[i] ?? 0, bounds[j] ?? text.length];
  }
  return [0, text.length];
}

export function expandDeleteRange(text: string, start: number, end: number): [number, number] {
  const safeStart = clampIndex(text, Math.min(start, end));
  const safeEnd = clampIndex(text, Math.max(start, end));

  if (safeStart === safeEnd) return [safeStart, safeEnd];

  const ranges = wordRanges(text);
  if (!ranges.length) return [safeStart, safeEnd];

  let firstTouched = -1;
  let lastTouched = -1;

  for (let i = 0; i < ranges.length; i++) {
    const [wordStart, wordEnd] = ranges[i] as [number, number];
    if (safeEnd <= wordStart || safeStart >= wordEnd) continue;
    if (firstTouched === -1) firstTouched = i;
    lastTouched = i;
  }

  if (firstTouched !== -1 && lastTouched !== -1) {
    return [ranges[firstTouched]![0], ranges[lastTouched]![1]];
  }

  const leftWordIndex = [...ranges.keys()].reverse().find((i) => (ranges[i]?.[1] ?? 0) <= safeStart) ?? -1;
  const rightWordIndex = ranges.findIndex(([, wordEnd], i) => {
    const wordStart = ranges[i]?.[0] ?? 0;
    return wordStart >= safeEnd || wordEnd > safeEnd;
  });

  if (leftWordIndex !== -1 && rightWordIndex !== -1 && leftWordIndex + 1 === rightWordIndex) {
    const [leftStart] = ranges[leftWordIndex] as [number, number];
    return [leftStart, safeEnd];
  }

  if (leftWordIndex !== -1) {
    const [leftStart] = ranges[leftWordIndex] as [number, number];
    return [leftStart, safeEnd];
  }

  if (rightWordIndex !== -1) {
    const [, rightEnd] = ranges[rightWordIndex] as [number, number];
    return [safeStart, rightEnd];
  }

  return [safeStart, safeEnd];
}

export function getBackspaceRange(text: string, index: number): [number, number] {
  const safeIndex = clampIndex(text, index);
  if (safeIndex === 0) return [0, 0];

  const ranges = wordRanges(text);
  if (!ranges.length) return [0, safeIndex];

  for (let i = ranges.length - 1; i >= 0; i--) {
    const [start, end] = ranges[i] as [number, number];
    if (safeIndex > start && safeIndex <= end) {
      return [start, safeIndex];
    }
    if (end <= safeIndex) {
      let deleteEnd = safeIndex;
      while (deleteEnd > end && !isWordChar(text[deleteEnd - 1] ?? "")) deleteEnd--;
      return [start, Math.max(end, deleteEnd === end ? safeIndex : safeIndex)];
    }
  }

  return [0, safeIndex];
}
